import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Field,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Text,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { Dismiss16Regular } from '@fluentui/react-icons';
import LoadingIcon from './LoadingIcon';
import AgreementPalette from './AgreementPalette';
import AgreementQuestions from './AgreementQuestions';
import DraftInsertPrompt, { DraftInsertAction } from './DraftInsertPrompt';
import {
  AgreementType,
  buildCustomAgreementType,
  Q_ADDITIONAL,
  ResolvedAgreementType,
} from '../../services/agreementTypes';
import {
  AgreementHistoryEntry,
  getLastAgreement,
  setLastAgreement,
} from '../../services/agreementHistory';
import {
  DraftGenerationResult,
  pollDraftGenerationResult,
  triggerDraftGeneration,
} from '../../services/wordDraftGeneration';
import { insertGeneratedAgreement } from '../../word/selection';
import { AuthUser } from '../../services/auth';
import { documentTypeForAgreementId, fetchDraftQuestions } from '../../services/wordDraftQuestions';
import { convertDocxToPreviewHtml } from '../../services/docxPreview';
import { isAccountPlanDisabledError, isFeatureQuotaDepletedError } from '../../services/planLimit';
import UpgradePrompt from './UpgradePrompt';
import { useUpgradeNavigation } from '../useUpgradeNavigation';

/* global HTMLTextAreaElement, console */

type Screen = 'select' | 'loadingQuestions' | 'questions' | 'loading' | 'result';

/**
 * Status messages shown while the backend generates the document. Generation
 * is an atomic async job (no partial output to stream), so the stages are
 * purely elapsed-time-based — a rough guess at what stage of drafting a
 * human would be at, not a real progress signal.
 */
function chooseDraftStatus(elapsedMs: number): string {
  if (elapsedMs < 3000) return 'Reading your inputs…';
  if (elapsedMs < 8000) return 'Sigi is sketching the agreement structure…';
  if (elapsedMs < 15000) return 'Considering jurisdiction and governing law…';
  if (elapsedMs < 25000) return 'Mapping out the required clauses…';
  if (elapsedMs < 40000) return 'Drafting the operative clauses…';
  if (elapsedMs < 60000) return 'Adding boilerplate and the signature block…';
  return 'Finalizing the document…';
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  intro: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  customRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  customActions: {
    display: 'flex',
    gap: '8px',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '10px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  loadingMeta: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    paddingLeft: '32px',
  },
  resultCard: {
    position: 'relative',
    padding: '12px',
    paddingRight: '32px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  dismiss: {
    position: 'absolute',
    top: '4px',
    right: '4px',
  },
  resultTitle: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: '8px',
  },
  summaryBox: {
    padding: '8px 10px',
    marginBottom: '10px',
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  summaryLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '4px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  previewPage: {
    maxHeight: '360px',
    overflow: 'auto',
    marginBottom: '10px',
    padding: '20px',
    backgroundColor: '#ffffff',
    color: '#000000',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: "'Times New Roman', Times, serif",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    // Mammoth's output is plain <p>/<h1-6>/<strong>/<em> tags with no
    // spacing of its own — add paragraph rhythm so it reads like a page.
    '& p': { margin: '0 0 10px 0' },
    '& h1, & h2, & h3': { margin: '0 0 10px 0' },
  },
});

interface DraftingTabProps {
  user: AuthUser | null;
  onAuthChange: (user: AuthUser | null) => void;
  /** The team's whole plan is disabled — parent collapses to the upgrade gate. */
  onPlanLimitHit: () => void;
}

const DraftingTab: React.FC<DraftingTabProps> = ({ user, onAuthChange, onPlanLimitHit }) => {
  const styles = useStyles();
  const [screen, setScreen] = useState<Screen>('select');
  const [activeType, setActiveType] = useState<ResolvedAgreementType | null>(null);
  const [customDescription, setCustomDescription] = useState('');
  const [generatedResult, setGeneratedResult] = useState<DraftGenerationResult | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [showInsertPrompt, setShowInsertPrompt] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set only when THIS feature's own metered quota runs out — other tabs stay usable.
  const [quotaLimitHit, setQuotaLimitHit] = useState(false);
  const [history, setHistory] = useState<AgreementHistoryEntry | null>(null);
  const { handleUpgradeClick, upgradeLoading } = useUpgradeNavigation(user, onAuthChange);

  // Loading status. `elapsedSec` drives the message picked from
  // chooseDraftStatus(). Refs hold live values the interval reads without
  // re-renders fighting the timer.
  const [draftStatus, setDraftStatus] = useState('');
  const [draftElapsedSec, setDraftElapsedSec] = useState(0);
  const draftStartRef = useRef(0);
  const draftTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Aborts an in-flight poll loop if the whole task pane unmounts mid-generation
  // (e.g. the add-in is closed). Tab switches no longer unmount this component.
  const genAbortRef = useRef<AbortController | null>(null);

  // Load history on mount.
  useEffect(() => {
    setHistory(getLastAgreement());
  }, []);

  const stopDraftTimer = useCallback(() => {
    if (draftTimerRef.current) {
      clearInterval(draftTimerRef.current);
      draftTimerRef.current = null;
    }
  }, []);

  // Cleanup on unmount in case a generation is still in flight.
  useEffect(() => {
    return () => {
      stopDraftTimer();
      genAbortRef.current?.abort();
    };
  }, [stopDraftTimer]);

  const loadQuestionsForType = useCallback(
    async (type: AgreementType): Promise<ResolvedAgreementType | null> => {
      if (!user) {
        setError('Sign in to draft agreements.');
        return null;
      }
      try {
        const documentType = documentTypeForAgreementId(type.id);
        const fetched = await fetchDraftQuestions(
          user.teamSlug,
          user.accessToken,
          documentType,
          type.isCustom ? type.customDescription : undefined,
        );
        if (fetched.length === 0) {
          setError("Signus didn't return any questions for this agreement type.");
          return null;
        }
        return { ...type, questions: [...fetched, Q_ADDITIONAL] };
      } catch (e) {
        if (isAccountPlanDisabledError(e)) {
          onPlanLimitHit();
        } else if (isFeatureQuotaDepletedError(e)) {
          setQuotaLimitHit(true);
        } else {
          setError(
            `Couldn't load questions from Signus: ${e instanceof Error ? e.message : String(e)}`,
          );
        }
        return null;
      }
    },
    [user, onPlanLimitHit],
  );

  const handleSelectBubble = useCallback(
    async (type: AgreementType) => {
      setError(null);
      setQuotaLimitHit(false);
      setScreen('loadingQuestions');
      const resolved = await loadQuestionsForType(type);
      if (!resolved) {
        setScreen('select');
        return;
      }
      setActiveType(resolved);
      setScreen('questions');
    },
    [loadQuestionsForType],
  );

  const handleSubmitCustom = useCallback(async () => {
    const trimmed = customDescription.trim();
    if (!trimmed) return;
    const customType = buildCustomAgreementType(trimmed);
    setError(null);
    setQuotaLimitHit(false);
    setScreen('loadingQuestions');
    const resolved = await loadQuestionsForType(customType);
    if (!resolved) {
      setScreen('select');
      return;
    }
    setActiveType(resolved);
    setScreen('questions');
  }, [customDescription, loadQuestionsForType]);

  const handleBack = useCallback(() => {
    setActiveType(null);
    setScreen('select');
  }, []);

  const handleGenerate = useCallback(
    async (answers: Record<string, string>) => {
      if (!activeType || !user) return;
      setError(null);
      setQuotaLimitHit(false);
      setScreen('loading');

      draftStartRef.current = Date.now();
      setDraftElapsedSec(0);
      setDraftStatus(chooseDraftStatus(0));

      draftTimerRef.current = setInterval(() => {
        const elapsedMs = Date.now() - draftStartRef.current;
        setDraftElapsedSec(Math.floor(elapsedMs / 1000));
        setDraftStatus(chooseDraftStatus(elapsedMs));
      }, 500);

      const controller = new AbortController();
      genAbortRef.current = controller;

      try {
        const questionsAndAnswers = activeType.questions.map(q => ({
          question: q.prompt,
          answer: (answers[q.id] ?? '').trim(),
        }));
        const generationId = await triggerDraftGeneration(
          user.teamSlug,
          user.accessToken,
          questionsAndAnswers,
        );
        const result = await pollDraftGenerationResult(
          user.teamSlug,
          user.accessToken,
          generationId,
          controller.signal,
        );
        setGeneratedResult(result);
        // Best-effort preview — no server-rendered page image exists for a
        // freshly generated draft, so render the actual docx content
        // client-side. A conversion failure shouldn't block the insert flow.
        try {
          setPreviewHtml(await convertDocxToPreviewHtml(result.contentBase64));
        } catch (e) {
          console.log('[DraftingTab] preview conversion failed:', e);
          setPreviewHtml(null);
        }
        // Persist to history (drives next render's bubble order/outline).
        setLastAgreement(activeType);
        setHistory(getLastAgreement());
        setScreen('result');
      } catch (e) {
        if (isAccountPlanDisabledError(e)) {
          onPlanLimitHit();
        } else if (isFeatureQuotaDepletedError(e)) {
          setQuotaLimitHit(true);
        } else {
          setError(e instanceof Error ? e.message : 'Generation failed.');
        }
        setScreen('questions');
      } finally {
        stopDraftTimer();
        genAbortRef.current = null;
      }
    },
    [activeType, user, stopDraftTimer, onPlanLimitHit],
  );

  const handleInsertClick = useCallback(() => {
    setShowInsertPrompt(true);
  }, []);

  const handleInsertResolve = useCallback(
    async (action: DraftInsertAction) => {
      setShowInsertPrompt(false);
      if (action === 'cancel' || !generatedResult) return;
      try {
        await insertGeneratedAgreement(generatedResult.contentBase64, action);
        // Reset to a clean state after successful insert.
        setGeneratedResult(null);
        setPreviewHtml(null);
        setActiveType(null);
        setCustomDescription('');
        setScreen('select');
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not insert into the document.');
      }
    },
    [generatedResult],
  );

  const handleDismissResult = useCallback(() => {
    setGeneratedResult(null);
    setPreviewHtml(null);
    setActiveType(null);
    setScreen('select');
  }, []);

  return (
    <div className={styles.root}>
      {quotaLimitHit ? (
        <UpgradePrompt onUpgrade={handleUpgradeClick} loading={upgradeLoading} />
      ) : (
        error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )
      )}

      {screen === 'select' && !user && (
        <MessageBar intent="warning">
          <MessageBarBody>Sign in to draft agreements.</MessageBarBody>
        </MessageBar>
      )}

      {screen === 'select' && user && (
        <>
          <Text className={styles.intro}>
            Pick an agreement type — or describe your own below. Signus will ask a few questions,
            then generate a complete draft to insert into the document.
          </Text>

          <AgreementPalette history={history} onSelect={handleSelectBubble} />

          <div className={styles.customRow}>
            <Field label="Or describe a custom agreement">
              <Textarea
                value={customDescription}
                placeholder='e.g. "Software beta-testing agreement for early-access users"'
                rows={2}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setCustomDescription(e.target.value)
                }
              />
            </Field>
            <div className={styles.customActions}>
              <Button
                appearance="primary"
                disabled={!customDescription.trim()}
                onClick={handleSubmitCustom}
              >
                Continue
              </Button>
            </div>
          </div>
        </>
      )}

      {screen === 'loadingQuestions' && (
        <div className={styles.loadingRow}>
          <LoadingIcon size={20} />
          <span>Loading questions…</span>
        </div>
      )}

      {screen === 'questions' && activeType && (
        <AgreementQuestions
          agreementType={activeType}
          onBack={handleBack}
          onSubmit={handleGenerate}
        />
      )}

      {screen === 'loading' && (
        <div className={styles.loadingBox}>
          <div className={styles.loadingRow}>
            <LoadingIcon size={24} />
            <span>{draftStatus || 'Drafting your agreement…'}</span>
          </div>
          <div className={styles.loadingMeta}>
            {draftElapsedSec > 0 ? `${draftElapsedSec}s` : 'starting…'}
          </div>
        </div>
      )}

      {screen === 'result' && activeType && generatedResult && (
        <div className={styles.resultCard}>
          <Button
            className={styles.dismiss}
            appearance="subtle"
            icon={<Dismiss16Regular />}
            size="small"
            aria-label="Dismiss"
            onClick={handleDismissResult}
          />
          <div className={styles.resultTitle}>{activeType.fullName}</div>
          {generatedResult.summarySpans.length > 0 && (
            <div className={styles.summaryBox}>
              <div className={styles.summaryLabel}>Summary</div>
              <div>
                {generatedResult.summarySpans.map((span, i) =>
                  span.style === 'bold' ? (
                    <strong key={i}>{span.text}</strong>
                  ) : (
                    <span key={i}>{span.text}</span>
                  ),
                )}
              </div>
            </div>
          )}
          {previewHtml ? (
            <div className={styles.previewPage} dangerouslySetInnerHTML={{ __html: previewHtml }} />
          ) : (
            <Text className={styles.intro}>
              Generated "{generatedResult.filename}" — click Insert to add it to the document.
            </Text>
          )}
          <div className={styles.actions}>
            <Button appearance="primary" onClick={handleInsertClick}>
              Insert into document
            </Button>
            <Button appearance="secondary" onClick={handleDismissResult}>
              Discard
            </Button>
          </div>
        </div>
      )}

      <DraftInsertPrompt open={showInsertPrompt} onResolve={handleInsertResolve} />
    </div>
  );
};

export default DraftingTab;
